import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useViewerStudent } from "../../auth/ViewAsContext";
import {
  Answers,
  FAIR_FORMS,
  FLOWCHART_URL,
  FORM_GROUPS,
  FORM_STATUS_LABEL,
  FormStatus,
  needsPreApproval,
  OCSEF_SOURCE,
} from "../../lib/fairForms";

/**
 * Requirements — the project sponsor and the ISEF paperwork, in one place.
 *
 * Sponsor: usable at any step (teachers need lead time). The letter is drafted
 * by AI and sent from Ardent Scholar through sponsor-request, which also logs
 * it. A student with no teacher sponsor can ask Dr. Li instead.
 *
 * Forms: readable and downloadable from the start, but the checkboxes and
 * statuses are read-only until the student reaches Step 6 (Step 5 approved).
 *
 * The row (project_requirement) is written by the student under RLS and
 * autosaved; letter_sent_at / dr_li_requested_at are only set by the function.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

type SponsorStatus =
  "not_asked" | "asked" | "agreed" | "declined" | "requested_dr_li";

const SPONSOR_STATUS_LABEL: Record<SponsorStatus, string> = {
  not_asked: "Not asked",
  asked: "Asked",
  agreed: "Agreed",
  declined: "Declined",
  requested_dr_li: "Requested Dr. Li",
};

interface FormEntry {
  needed?: boolean;
  status?: FormStatus;
}

interface Draft {
  to: string;
  subject: string;
  body: string;
}

async function callFn(body: Record<string, unknown>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/sponsor-request`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${session?.access_token ?? ""}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    if (res.status === 401 && !data?.error)
      throw new Error(
        "Your sign-in has expired. Refresh the page and try again.",
      );
    throw new Error(data?.error ?? `Request failed (${res.status})`);
  }
  return data;
}

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

export default function RequirementsPage() {
  const {
    studentId,
    viewerIsStudent,
    loading: vasLoading,
  } = useViewerStudent();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<{
    project_id: string;
    current_step: number | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle",
  );

  // sponsor
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [subjectTaught, setSubjectTaught] = useState("");
  const [status, setStatus] = useState<SponsorStatus>("not_asked");
  const [letterSubject, setLetterSubject] = useState("");
  const [letter, setLetter] = useState("");
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [drLiAt, setDrLiAt] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | "draft" | "send" | "drli">(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [drLiOpen, setDrLiOpen] = useState(false);
  const [drLiNote, setDrLiNote] = useState("");

  const navigate = useNavigate();

  // forms
  const [ack, setAck] = useState<string | null>(null);
  const [triage, setTriage] = useState<Answers>({});
  /* The Step 6 wizard's last run, for provenance. This page tracks; Step 6 decides. */
  const [wizardRanAt, setWizardRanAt] = useState<string | null>(null);
  const [wizardAppliedAt, setWizardAppliedAt] = useState<string | null>(null);
  const [forms, setForms] = useState<Record<string, FormEntry>>({});
  const [asking, setAsking] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft[]>>({});
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (vasLoading || !studentId) return;
    (async () => {
      const { data: proj } = await supabase
        .from("project")
        .select("project_id, current_step")
        .eq("student_id", studentId)
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(1);
      const p = proj?.[0] ?? null;
      setProject(p);
      if (p) {
        const { data: row } = await supabase
          .from("project_requirement")
          .select("*")
          .eq("project_id", p.project_id)
          .maybeSingle();
        if (row) {
          setFirst(row.sponsor_first ?? "");
          setLast(row.sponsor_last ?? "");
          setEmail(row.sponsor_email ?? "");
          setSubjectTaught(row.sponsor_subject ?? "");
          setStatus((row.sponsor_status as SponsorStatus) ?? "not_asked");
          setLetter(row.letter_draft ?? "");
          setSentAt(row.letter_sent_at ?? null);
          setDrLiAt(row.dr_li_requested_at ?? null);
          setAck(row.forms_ack_at ?? null);
          setTriage(row.triage ?? {});
          setForms(row.forms ?? {});
          setWizardRanAt(row.wizard?.ran_at ?? null);
          setWizardAppliedAt(row.wizard?.applied_at ?? null);
        }
      }
      setLoading(false);
    })();
  }, [studentId, vasLoading]);

  const editable = viewerIsStudent;
  const locked = (project?.current_step ?? 1) < 6;
  const formsEditable = editable && !locked;

  // ── autosave ────────────────────────────────────────────────────────────
  /* Objects compared by value, so a re-render with equal answers doesn't save. */
  const triageKey = JSON.stringify(triage);
  const formsKey = JSON.stringify(forms);
  const firstRun = useRef(true);
  useEffect(() => {
    if (loading || !project || !editable) return;
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setSaveState("saving");
    const t = setTimeout(async () => {
      const { error: e } = await supabase.from("project_requirement").upsert(
        {
          project_id: project.project_id,
          student_id: studentId,
          sponsor_first: first,
          sponsor_last: last,
          sponsor_email: email,
          sponsor_subject: subjectTaught,
          sponsor_status: status,
          letter_draft: letter,
          forms_ack_at: ack,
          triage,
          forms,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "project_id" },
      );
      setSaveState(e ? "idle" : "saved");
      if (e) setError(e.message);
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    first,
    last,
    email,
    subjectTaught,
    status,
    letter,
    ack,
    triageKey,
    formsKey,
  ]);

  // ── sponsor actions ─────────────────────────────────────────────────────
  async function draftLetter() {
    setBusy("draft");
    setError(null);
    try {
      const r = await callFn({
        action: "draft_letter",
        sponsor_first: first,
        sponsor_last: last,
        sponsor_subject: subjectTaught,
      });
      setLetterSubject(r.subject);
      setLetter(r.body);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function sendLetter() {
    setConfirmSend(false);
    setBusy("send");
    setError(null);
    try {
      const r = await callFn({
        action: "send",
        sponsor_first: first,
        sponsor_last: last,
        sponsor_email: email,
        sponsor_subject: subjectTaught,
        subject: letterSubject,
        body: letter,
      });
      setSentAt(r.sent_at);
      setStatus("asked");
      setNotice(
        `Sent to ${r.sent_to}. ${r.cc?.length ? `Copies went to ${r.cc.join(", ")}.` : ""} Replies go to your email.`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function requestDrLi() {
    setDrLiOpen(false);
    setBusy("drli");
    setError(null);
    try {
      const r = await callFn({ action: "request_drli", note: drLiNote });
      setDrLiAt(r.requested_at);
      setStatus("requested_dr_li");
      setNotice("Your request went to Dr. Li. He will reply to your email.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function draftAsk(formKey: string) {
    setAsking(formKey);
    setError(null);
    try {
      const r = await callFn({ action: "draft_ask", form_key: formKey });
      setDrafts((d) => ({ ...d, [formKey]: r.drafts }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAsking(null);
    }
  }

  async function copy(id: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
    } catch {
      setError("Could not copy — select the text and copy it instead.");
    }
  }

  const setForm = (key: string, patch: FormEntry) =>
    setForms((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  if (vasLoading || loading)
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (!project)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No active project found.</Alert>
      </Box>
    );

  const canDraft = !!last.trim() && editable;
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const canSend =
    editable &&
    !!first.trim() &&
    !!last.trim() &&
    validEmail &&
    letter.trim().length >= 40;

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 900, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
          Requirements
        </Typography>
        {editable && saveState !== "idle" && (
          <Typography variant="caption" color="text.secondary">
            {saveState === "saving" ? "Saving…" : "✓ All changes saved"}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your sponsor and the paperwork your science fair requires, and
        learn how to ask each person for what you need.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      )}

      {/* ── Sponsor ──────────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
            Science teacher sponsor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Science fairs require an adult sponsor — usually a science teacher
            at your school — who reviews your research plan and is your adult
            contact for the fair.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              mb: 2,
            }}
          >
            <TextField
              size="small"
              label="Teacher's first name"
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              disabled={!editable}
            />
            <TextField
              size="small"
              label="Last name"
              value={last}
              onChange={(e) => setLast(e.target.value)}
              disabled={!editable}
            />
            <TextField
              size="small"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!editable}
            />
            <TextField
              size="small"
              label="Subject taught"
              value={subjectTaught}
              onChange={(e) => setSubjectTaught(e.target.value)}
              disabled={!editable}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={draftLetter}
              disabled={!canDraft || !!busy}
            >
              {busy === "draft" ? (
                <>
                  <CircularProgress size={14} sx={{ mr: 1 }} />
                  Drafting…
                </>
              ) : letter ? (
                "Draft again with AI"
              ) : (
                "Draft my request with AI"
              )}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Status:
            </Typography>
            <Select
              size="small"
              value={status}
              disabled={!editable}
              onChange={(e) => setStatus(e.target.value as SponsorStatus)}
              sx={{ minWidth: 170 }}
            >
              {(Object.keys(SPONSOR_STATUS_LABEL) as SponsorStatus[]).map(
                (s) => (
                  <MenuItem key={s} value={s}>
                    {SPONSOR_STATUS_LABEL[s]}
                    {s === "asked" && sentAt ? ` on ${fmtDate(sentAt)}` : ""}
                  </MenuItem>
                ),
              )}
            </Select>
          </Box>

          {(letter || letterSubject) && (
            <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
              <TextField
                size="small"
                label="Subject"
                value={letterSubject}
                onChange={(e) => setLetterSubject(e.target.value)}
                disabled={!editable}
                /* Only the letter body is saved; a blank subject sends with the
                   default, so show it rather than an empty box. */
                placeholder="Request: adult sponsor for my science fair project"
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                multiline
                minRows={10}
                label="Your letter"
                value={letter}
                onChange={(e) => setLetter(e.target.value)}
                disabled={!editable}
                helperText="Edit it into your own words. Change “Mr./Ms.” to the right title."
              />
            </Box>
          )}

          {letter && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                onClick={() => setConfirmSend(true)}
                disabled={!canSend || !!busy}
              >
                {busy === "send" ? "Sending…" : "Send from Ardent Scholar"}
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.75 }}
              >
                Sent from Ardent Scholar on your behalf. Replies go to your
                email, and you and your parent get a copy.
                {sentAt ? ` Last sent ${fmtDate(sentAt)}.` : ""}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Don't have a science teacher who can sponsor you?
            </Typography>
            <Button
              size="small"
              onClick={() => setDrLiOpen(true)}
              disabled={!editable || !!busy}
            >
              {busy === "drli" ? "Sending…" : "Request Dr. Li as my sponsor"}
            </Button>
            {drLiAt && (
              <Typography variant="caption" color="text.secondary">
                Requested {fmtDate(drLiAt)}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Forms ────────────────────────────────────────────────────────── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
        Required forms
      </Typography>
      <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
        These are ISEF (International Science and Engineering Fair) forms for
        2026–2027. Science fairs affiliated with ISEF, including OCSEF, require
        them.{" "}
        <strong>
          You are responsible for using the exact, current forms your fair
          requires.
        </strong>
      </Alert>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Checkbox
            checked={!!ack}
            disabled={!formsEditable}
            onChange={(e) =>
              setAck(e.target.checked ? new Date().toISOString() : null)
            }
          />
        }
        label={
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            I will use the exact, current-year forms required by my science
            fair.
          </Typography>
        }
      />

      {locked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can read and download these forms now. Tracking opens when you
          reach Step 6.
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Which forms apply to you?
          </Typography>
          {wizardAppliedAt ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {needsPreApproval(triage) ? (
                  <>
                    Your answers in Step 6 say your project{" "}
                    <strong>needs SRC pre-approval</strong>. The forms marked
                    below came from those answers — get them approved before you
                    start experimenting.
                  </>
                ) : (
                  <>
                    Your answers in Step 6 say your project{" "}
                    <strong>does not need SRC pre-approval</strong>. The forms
                    marked below came from those answers.
                  </>
                )}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                Answered in Step 6 · Pre-Approval on{" "}
                {new Date(wizardAppliedAt).toLocaleDateString()}. Change an
                answer there and apply it again to update this list.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {locked
                ? "When you reach Step 6, the Pre-Approval tab reads your project and works out which OCSEF forms you need. Its answers land here, where you track them."
                : "Start in Step 6 · Pre-Approval. It reads your project, walks you through OCSEF's questions, and the forms you confirm show up here to track."}
            </Typography>
          )}
          <Button
            size="small"
            variant={wizardAppliedAt ? "outlined" : "contained"}
            disabled={locked}
            onClick={() => navigate("/step/6")}
          >
            {wizardAppliedAt
              ? "Review my answers in Step 6"
              : "Go to Step 6 · Pre-Approval"}
          </Button>
          {wizardRanAt && !wizardAppliedAt && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ display: "block", mt: 1 }}
            >
              You have unapplied answers in Step 6 — confirm them there to update
              this checklist.
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            {OCSEF_SOURCE}{" "}
            <a href={FLOWCHART_URL} target="_blank" rel="noopener noreferrer">
              Open OCSEF's flowchart
            </a>
            .
          </Typography>
        </CardContent>
      </Card>

      {FORM_GROUPS.map((group) => (
        <Box key={group} sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700 }}
          >
            {group}
          </Typography>
          {FAIR_FORMS.filter((f) => f.group === group).map((f) => {
            const entry = forms[f.key] ?? {};
            const suggested = f.suggested(triage);
            return (
              <Card key={f.key} variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {f.code} · {f.title}
                    </Typography>
                    {suggested && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label="Suggested for you"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>For:</strong> {f.purpose}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Signs:</strong> {f.signs}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mb: f.tips.length ? 0.5 : 1 }}
                  >
                    <strong>When:</strong> {f.when}
                  </Typography>
                  {f.tips.length > 0 && (
                    <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                      {f.tips.map((t) => (
                        <Typography
                          component="li"
                          variant="body2"
                          color="text.secondary"
                          key={t}
                        >
                          {t}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {f.ask && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      <strong>Ask:</strong> {f.ask}
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </Button>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!entry.needed}
                          disabled={!formsEditable}
                          onChange={(e) =>
                            setForm(f.key, { needed: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I need this form
                        </Typography>
                      }
                    />
                    <Select
                      size="small"
                      value={entry.status ?? "not_started"}
                      disabled={!formsEditable || !entry.needed}
                      onChange={(e) =>
                        setForm(f.key, { status: e.target.value as FormStatus })
                      }
                      sx={{ minWidth: 150 }}
                    >
                      {(Object.keys(FORM_STATUS_LABEL) as FormStatus[]).map(
                        (s) => (
                          <MenuItem key={s} value={s}>
                            {FORM_STATUS_LABEL[s]}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                    {f.ask && (
                      <Button
                        size="small"
                        onClick={() => draftAsk(f.key)}
                        disabled={!formsEditable || !!asking}
                      >
                        {asking === f.key ? (
                          <>
                            <CircularProgress size={12} sx={{ mr: 1 }} />
                            Drafting…
                          </>
                        ) : drafts[f.key] ? (
                          "Draft again"
                        ) : (
                          "Draft a request"
                        )}
                      </Button>
                    )}
                  </Box>

                  {(drafts[f.key] ?? []).map((d, i) => {
                    const id = `${f.key}-${i}`;
                    const full = `Subject: ${d.subject}\n\n${d.body}`;
                    return (
                      <Box
                        key={id}
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: "grey.50",
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            To {d.to}
                          </Typography>
                          <Button size="small" onClick={() => copy(id, full)}>
                            {copied === id ? "Copied" : "Copy"}
                          </Button>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        >
                          {d.subject}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {d.body}
                        </Typography>
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ))}

      {/* ── dialogs ──────────────────────────────────────────────────────── */}
      <Dialog open={confirmSend} onClose={() => setConfirmSend(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Send your request?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            Your letter goes to{" "}
            <strong>
              {first} {last}
            </strong>{" "}
            at <strong>{email}</strong> from Ardent Scholar on your behalf. You
            and your parent get a copy, and replies go to your email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSend(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendLetter}>
            Send to {first} {last}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={drLiOpen}
        onClose={() => setDrLiOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Request Dr. Li as your sponsor
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
            Dr. Li will get your name, school, project, and fair, and will reply
            to your email.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Note to Dr. Li (optional)"
            placeholder="e.g. My school doesn't have a science teacher who sponsors fair projects."
            value={drLiNote}
            onChange={(e) => setDrLiNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrLiOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={requestDrLi}>
            Send request to Dr. Li
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
